import React, { useId, useState, useEffect, useRef } from 'react';
import { onCelebrate } from './celebrate';
import { onPointerMove } from './pointer';

/* ══════════════════════════════════════════════════════════════
   THE MARK

   Drawn in code rather than shipped as an image, so it can react — it
   blinks, glances around, and can be told to look pleased when the
   learner gets something right.

   Three things that matter if you edit this:

   • The gradient and filter ids must be unique per instance. SVG ids
     are global to the document, so two marks on one page sharing an id
     means the second silently steals the first's fill. useId() handles
     it — do not hardcode them back.

   • The idle loop is randomised, never a fixed cycle. A mark that
     blinks on a metronome reads as a broken GIF; irregular timing
     reads as alive.

   • Everything is in the blob's own 386-unit space. If you move the
     eye, keep it there rather than converting to percentages — the
     numbers below are already tuned to the blob's centre of mass,
     which is not quite its bounding-box centre.
══════════════════════════════════════════════════════════════ */

/* The blob: four lobes with a deliberate hand-drawn asymmetry, which is
   what stops it reading as a piece of geometry. */
const BLOB = `M 193 0 C 168 0, 151 12, 138 35 C 121 64, 99 88, 69 106
  C 40 124, 13 143, 5 169 C -4 198, 5 224, 28 244 C 45 260, 70 271, 91 291
  C 111 310, 126 337, 143 359 C 157 378, 175 386, 193 386
  C 217 386, 235 374, 249 353 C 264 330, 279 309, 301 292
  C 321 277, 348 265, 365 246 C 383 226, 389 201, 381 174
  C 373 146, 350 126, 321 108 C 293 91, 270 67, 253 38
  C 240 15, 221 0, 193 0 Z`;

/* Warm at the top cooling to a deep burnt orange at the base — the same
   move the reference makes inside its own hue family. */
const GRADIENT = [
  ['0%',   '#FBBF24'],
  ['30%',  '#FB923C'],
  ['65%',  '#F97316'],
  ['100%', '#DC4C0A'],
];

/* The catalogue. Each entry names up to three CSS classes — one per
   transform channel, so a move and a rotate can run together without
   overwriting each other's `transform`. `eye` pins an expression for the
   duration; `ms` is how long a one-shot lasts and must match the CSS.
   Loops have no `ms`: they run until the caller changes `animation`. */
const ANIMATIONS = {
  celebrate: { move: 'kitsu-lift', rotate: 'kitsu-wheel', eye: 'happy', ms: 2100 },
  bounce:    { move: 'kitsu-bounce',  eye: 'happy', ms: 900 },
  shake:     { move: 'kitsu-shake',   eye: 'sad',   ms: 700 },
  nod:       { move: 'kitsu-nod',     eye: 'happy', ms: 800 },
  wobble:    { scale: 'kitsu-wobble', ms: 800 },
  pop:       { scale: 'kitsu-pop',    ms: 600 },

  float:   { move: 'kitsu-float',    loop: true },
  breathe: { scale: 'kitsu-breathe', loop: true },
  pulse:   { scale: 'kitsu-pulse',   loop: true },
  spin:    { rotate: 'kitsu-spin',   loop: true },
  sleep:   { scale: 'kitsu-drowse',  eye: 'blink', loop: true },
};

export const ANIMATION_NAMES = Object.keys(ANIMATIONS);


/* How close the cursor has to get before the eye starts following it.
   Scaled off the mark's own size so a 16px favicon-ish mark does not
   claim the same catchment as a 200px hero. */
const TRACK_RADIUS_MULTIPLE = 4.5;
/* Pupil travel, in the blob's 386-unit space. Beyond about 24 the pupil
   starts to touch the edge of its well. */
const GAZE_X = 22;
const GAZE_Y = 17;

const PUPIL = '#0d1418';
/* The app's own text colour rather than pure white — #fff next to a warm
   orange reads clinical, and it is the one value on the page that would
   be brighter than anything else in the UI. */
const WELL = '#f7f3ea';

/* The blob never moves; the eye carries all the character. That is what
   keeps the mark feeling stable while still feeling alive. */
function Eye({ state, dx = 0, dy = 0, alert = false }) {
  /* A squircle, sitting between a square and a circle: on a 136 box, rx 0
     is a square and rx 68 a circle, so 38 lands just past the midpoint.
     136 fills the blob's centre without crowding the arms — 148 started to. */
  const well = <rect x="125" y="125" width="136" height="136" rx="38" fill={WELL} />;

  if (state === 'blink') {
    return (
      <>
        {well}
        <rect x="146" y="180" width="94" height="25" rx="12.5" fill={PUPIL} />
      </>
    );
  }
  if (state === 'sad') {
    return (
      <>
        {well}
        <path
          d="M148 178 C167 222, 219 222, 238 178"
          stroke={PUPIL}
          strokeWidth="27"
          fill="none"
          strokeLinecap="round"
        />
      </>
    );
  }
  if (state === 'happy') {
    return (
      <>
        {well}
        <path
          d="M148 211 C167 167, 219 167, 238 211"
          stroke={PUPIL}
          strokeWidth="27"
          fill="none"
          strokeLinecap="round"
        />
      </>
    );
  }
  return (
    <>
      {well}
      {/* A slightly smaller pupil reads as a widened eye — the tell that
          the mark has noticed you. */}
      <ellipse
        cx={193 + dx}
        cy={193 + dy}
        rx={alert ? 31 : 35}
        ry={alert ? 35 : 39}
        fill={PUPIL}
        style={{ transition: 'cx 0.12s ease-out, cy 0.12s ease-out, rx 0.18s ease, ry 0.18s ease' }}
      />
    </>
  );
}

export default function KitsuMark({
  size = 32,
  animated = true,
  /* Pin a state to override the idle loop — 'happy' after a correct
     answer, 'blink' while something loads. */
  expression = null,
  glow = true,
  /* Drops the gradient for a single flat fill. Useful anywhere the mark
     sits on a busy background or has to print in one colour. */
  flat = false,
  /* Flip this truthy to fire the celebrate animation once — the blob
     lifts, spins down like a wheel and settles back with a smile. Flip
     it again to replay. Good after a correct answer or a finished
     lesson. */
  celebrate = false,
  /* Subscribe to the app-wide celebrate bus instead of being driven by
     a prop. The sidebar mark uses this so a finished lesson anywhere
     reaches it without threading props through the tree. */
  listen = false,
  /* One of ANIMATION_NAMES. Loops run until this changes; one-shots run
     once and hand back to the idle loop. Bump `animationRun` to replay
     the same one-shot. */
  animation = null,
  animationRun = 0,
  /* Eye follows the cursor when it comes near, the blob perks up on
     hover, and clicking it celebrates. */
  interactive = true,
  className,
  style,
  ...rest
}) {
  const uid = useId().replace(/:/g, '');
  const [state, setState] = useState('open');
  const [gaze, setGaze] = useState({ dx: 0, dy: 0 });
  const timers = useRef([]);
  /* Bumping this remounts the animated groups, which is what restarts a
     CSS animation — re-applying the same class alone would not. */
  const [runId, setRunId] = useState(0);
  const [playing, setPlaying] = useState(null);   // animation name, or null
  const lastCelebrate = useRef(celebrate);
  const svgRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  /* Held in a ref as well as state: the idle loop needs to know whether
     the cursor has the eye without re-subscribing every time it moves. */
  const tracking = useRef(false);

  /* Split deliberately into two effects.

     The trigger has no cleanup and is guarded by a ref, so React's
     StrictMode double-invoke cannot bump runId twice — which it did,
     visibly restarting the spin a second into it.

     The reset owns the timeout separately, so the double-invoke's
     clear-then-rerun still leaves a live timer. Folding these together
     leaves `cheering` stuck true forever in development. */
  useEffect(() => {
    if (celebrate === lastCelebrate.current) return;
    lastCelebrate.current = celebrate;
    if (!celebrate) return;
    setRunId(n => n + 1);
    setPlaying('celebrate');
  }, [celebrate]);

  /* One-shots hand back to the idle loop when their CSS finishes; loops
     stay until something replaces them. */
  useEffect(() => {
    if (!playing) return undefined;
    const ms = ANIMATIONS[playing]?.ms;
    if (!ms) return undefined;
    const t = setTimeout(() => setPlaying(null), ms);
    return () => clearTimeout(t);
  }, [playing]);

  useEffect(() => {
    if (!interactive) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return undefined;

    const radius = size * TRACK_RADIUS_MULTIPLE;
    return onPointerMove((px, py) => {
      const el = svgRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width) return;                       // hidden or not laid out
      const dx = px - (r.left + r.width / 2);
      const dy = py - (r.top + r.height / 2);
      const dist = Math.hypot(dx, dy);

      if (dist > radius) {
        if (tracking.current) {
          tracking.current = false;
          setGaze({ dx: 0, dy: 0 });              // look back at the reader
        }
        return;
      }

      tracking.current = true;
      /* Full deflection over most of the catchment, fading to nothing at
         its edge — so the eye eases in as you approach instead of
         snapping the instant you cross the boundary, and points hardest
         when the cursor is right beside it. Scaling the other way round
         (up with distance) looks wrong: the eye barely reacts to a
         cursor sitting next to it and stares hardest at one far away. */
      const strength = Math.min(1, Math.max(0, (radius - dist) / (radius * 0.4)));
      setGaze({
        dx: Math.round((dx / (dist || 1)) * GAZE_X * strength),
        dy: Math.round((dy / (dist || 1)) * GAZE_Y * strength),
      });
    });
  }, [interactive, size]);

  /* Remounting the animated groups is what replays a CSS animation —
     re-applying the same class alone would not. */
  useEffect(() => {
    if (!animation) return undefined;
    setRunId(n => n + 1);
    return undefined;
  }, [animation, animationRun]);

  useEffect(() => {
    if (!listen) return undefined;
    return onCelebrate(name => {
      if (!ANIMATIONS[name]) return;
      setRunId(n => n + 1);
      setPlaying(name);
    });
  }, [listen]);

  useEffect(() => {
    if (!animated || expression || playing || animation) return undefined;
    /* Someone who asked their OS to reduce motion gets a still mark,
       not a twitching one. */
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return undefined;

    let alive = true;
    const sleep = ms => new Promise(res => {
      timers.current.push(setTimeout(res, ms));
    });

    (async function idle() {
      while (alive) {
        await sleep(1600 + Math.random() * 2600);
        if (!alive) return;
        const roll = Math.random();
        if (roll < 0.45) {
          setState('blink'); await sleep(110); if (alive) setState('open');
        } else if (roll < 0.8) {
          /* Skip the idle glance while the cursor has the eye — the two
             would fight over the same pupil. Blinks are fine either way. */
          if (tracking.current) continue;
          const dx = Math.random() < 0.5 ? -14 : 14;
          setGaze({ dx, dy: 0 }); await sleep(700);
          if (!alive) return;
          setState('blink'); await sleep(100);
          if (!alive) return;
          setState('open'); setGaze({ dx: 0, dy: 0 });
        } else {
          setState('happy'); await sleep(950); if (alive) setState('open');
        }
      }
    })();

    return () => {
      alive = false;
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [animated, expression, playing, animation]);

  /* `playing` is whatever the bus, a click, or the celebrate prop last
     fired; `animation` is the explicit prop. The prop wins, so a mark
     told to `spin` while loading is not interrupted by a celebration
     arriving from elsewhere. */
  const active = ANIMATIONS[animation] || ANIMATIONS[playing] || null;
  const shown = expression || active?.eye || state;

  /* overflow:visible on the <svg> below is load-bearing. The celebrate lift
     travels 42 units above the blob — well outside the viewBox — and SVG
     clips to its viewport by default, which sliced the top off the mark
     mid-animation. Letting it overflow beats padding the viewBox, which
     would have shrunk the mark everywhere to make room for a rare
     animation. `style` is destructured out of the props spread so a caller
     passing one cannot silently drop it. */
  const fill = flat ? '#F97316' : `url(#kgrad-${uid})`;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="-8 -8 402 402"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="KitsuSpeak"
      style={{ overflow: 'visible', ...style }}
      className={[className, interactive ? 'kitsu-interactive' : null].filter(Boolean).join(' ') || undefined}
      data-hovered={interactive && hovered ? '' : undefined}
      onMouseEnter={interactive ? () => setHovered(true) : undefined}
      onMouseLeave={interactive ? () => { setHovered(false); } : undefined}
      onClick={interactive ? () => { setRunId(n => n + 1); setPlaying('celebrate'); } : undefined}
      {...rest}
    >
      <defs>
        {!flat && (
          <linearGradient id={`kgrad-${uid}`} x1="193" y1="0" x2="193" y2="386" gradientUnits="userSpaceOnUse">
            {GRADIENT.map(([offset, color]) => (
              <stop key={offset} offset={offset} stopColor={color} />
            ))}
          </linearGradient>
        )}
        {glow && (
          <filter id={`kglow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="14" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* The lift carries the whole mark; the wheel turns only the body,
          so the eye stays upright while the blob spins around it. The
          translate/­translate-back sandwich puts the rotation origin on
          the blob's centre without depending on transform-origin. */}
      {/* Three channels, outermost first: move, then scale, then rotate.
          The eye lives inside scale but outside rotate — it squashes with
          the blob but stays upright while the body spins around it. The
          translate/translate-back sandwich puts the scale and rotation
          origins on the blob's centre. */}
      <g key={runId} className={active?.move}>
        <g transform="translate(193 193)">
          <g className={active?.scale}>
            <g className={active?.rotate}>
              <g transform="translate(-193 -193)">
                <g filter={glow ? `url(#kglow-${uid})` : undefined}>
                  <path fill={fill} d={BLOB} />
                </g>
              </g>
            </g>
            <g transform="translate(-193 -193)">
              <Eye state={shown} dx={gaze.dx} dy={gaze.dy} alert={hovered} />
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
